/*
arr-species.json loader — mirror Android `ArrSpeciesIndex` (cf.
`data/ArrSpeciesIndex.kt` + `BadgeEvaluator.evaluateFamilierArr` v1.4.0).
Dénominateur des 22 badges « Familier du [arr] » + source des
centroïdes par arr pour la Recherche universelle.

Format de l'asset v1.4.0 (généré par `tools/build_dataset.py` côté
Android) :

	{ "1":  { "remarquable_ids": [230281, …],     "centroid": [2.34, 48.86] },
	  "2":  { "remarquable_ids": [],              "centroid": [2.34, 48.87] },
	  ...
	  "vincennes": { "remarquable_ids": [...], "centroid": [...] },
	  "boulogne":  { "remarquable_ids": [...], "centroid": [...] } }

Chaque entrée : slug ArrKey → `{ remarquableArbreIds: Set<arbreId>, centroid: [lon, lat] }`.

**Breaking change v1.2.0 → v1.4.0** : le format `Record<slug, sk[]>` (liste
des espèces recensées dans l'arr) est remplacé par ce nouveau shape.
Side effect : les badges Familier arr changent de critère (couverture
des arbres remarquables physiques au lieu de couverture des espèces),
2 arrs sans remarquables (2e, 6e) deviennent structurellement
inunlockables — dénominateur Profile « Complétés » = 20 au lieu de 22.
*/

import { fetchJson } from './fetchJson'
import { arrKeyFromSlug, arrKeyIdSlug, arrKeySortKey, type ArrKey } from './arrKey'

const kAssetPath = '/data/arr-species.json'

export interface ArrInfo
{
	remarquableArbreIds: ReadonlySet<number>
	centroid: readonly [number, number]  // [lon, lat], moyenne arithmétique des arbres de l'arr (pré-cuit Android)
}

interface RawArrInfo
{
	remarquable_ids: number[]
	centroid: [number, number]
}

type RawIndex = Record<string, RawArrInfo>

let fBySlug: Map<string, ArrInfo> | null = null  // memoized slug -> ArrInfo
let fKeys: ArrKey[] | null = null  // memoized list of ArrKey, sorted canonical order
let fKeysWithRemarquables: ArrKey[] | null = null  // memoized, filtré sur remarquableArbreIds.size > 0
let fLoading: Promise<void> | null = null  // dedup concurrent callers

/*
Ensure the arr-species index is loaded. Idempotent. Builds the slug→ArrInfo
map + the sorted ArrKey list + the filtered « with remarquables » list in
a single pass.
*/
export async function ensureArrSpecies(): Promise<void>
{
	if (fBySlug) return
	if (fLoading) return fLoading
	fLoading = (async () => {
		const vRaw = await fetchJson<RawIndex>(kAssetPath)
		const vMap = new Map<string, ArrInfo>()
		const vKeys: ArrKey[] = []
		const vKeysWithRem: ArrKey[] = []
		for (const [vSlug, vRawInfo] of Object.entries(vRaw))
		{
			const vKey = arrKeyFromSlug(vSlug)
			if (!vKey) continue
			const vInfo: ArrInfo = {
				remarquableArbreIds: new Set<number>(vRawInfo.remarquable_ids),
				centroid: vRawInfo.centroid,
			}
			vMap.set(vSlug, vInfo)
			vKeys.push(vKey)
			if (vInfo.remarquableArbreIds.size > 0) vKeysWithRem.push(vKey)
		}
		vKeys.sort((a, b) => arrKeySortKey(a) - arrKeySortKey(b))
		vKeysWithRem.sort((a, b) => arrKeySortKey(a) - arrKeySortKey(b))
		fBySlug = vMap
		fKeys = vKeys
		fKeysWithRemarquables = vKeysWithRem
		fLoading = null
	})()
	return fLoading
}

/*
Sorted ArrKey list — canonical Android order 1..20 → Vincennes →
Boulogne (« other » jamais présent dans l'asset). Empty before
`ensureArrSpecies()`.
*/
export function arrSpeciesKeys(): ArrKey[]
{
	return fKeys ?? []
}

/*
Sous-ensemble d'arrSpeciesKeys() filtré sur les arrs portant au moins un
arbre remarquable. Dénominateur de la barre « Arrondissements complétés »
côté Profile (v1.4.0 : 20 au lieu de 22 — 2e et 6e arr sans remarquables).
*/
export function arrKeysWithRemarquables(): ArrKey[]
{
	return fKeysWithRemarquables ?? []
}

/*
Set des arbreIds remarquables physiques de l'arr. Cible du badge
« Familier du [arr] » v1.4.0. Empty Set pour `{kind:'other'}`, 2e, 6e ou
toute key absente de l'asset.
*/
export function arrRemarquableArbreIdsOf(inKey: ArrKey): ReadonlySet<number>
{
	if (!fBySlug) return new Set()
	return fBySlug.get(arrKeyIdSlug(inKey))?.remarquableArbreIds ?? new Set()
}

/*
Centroïde [lon, lat] de l'arr (moyenne arithmétique des coordonnées
arbres pré-cuite côté Android). Sert au fly-to z13 depuis la Recherche
universelle. null pour `{kind:'other'}` ou key absente.
*/
export function arrCentroidOf(inKey: ArrKey): readonly [number, number] | null
{
	if (!fBySlug) return null
	return fBySlug.get(arrKeyIdSlug(inKey))?.centroid ?? null
}
